import type { SettingKey, TokenFeature } from "metabase-types/api";

export const EMBEDDING_SDK_PORTAL_ROOT_ELEMENT_ID = "metabase-sdk-portal-root";

type InternalSdkConfig = {
  isEmbeddingSdk: boolean;
  isMcpApp: boolean;
  isDataApp: boolean;
  isDataAppDev: boolean;
  metabaseClientRequestHeader:
    | "embedding-sdk-react"
    | "embedding-simple"
    | "mcp-apps"
    | "data-app";
  metabaseClientRequestIdentifier: string | undefined;
  enableEmbeddingSettingKey: "enable-embedding-modular";
  tokenFeatureKey: "embedding_sdk" | "embedding_simple";
};

export const EMBEDDING_SDK_CONFIG: InternalSdkConfig = {
  /**
   * Whether we are in the Embedding SDK or its derivatives
   * such as sdk-based iframe embedding.
   **/
  isEmbeddingSdk: false,

  /**
   * Whether we are in the MCP Apps context.
   **/
  isMcpApp: false,

  /**
   * Whether we are in the Data App context.
   **/
  isDataApp: false,

  /**
   * Whether the data app runs in the local development flow. Reported to the
   * backend via the X-NubrixAI-Embedded-Preview header, so its traffic is
   * recorded as the "data-app-preview" embedding client.
   **/
  isDataAppDev: false,

  /**
   * Which X-NubrixAI-Client header to use for requests to the NubrixAI instance?
   */
  metabaseClientRequestHeader: "embedding-sdk-react",

  /**
   * Which X-NubrixAI-Client-Identifier header to use for requests to the
   * NubrixAI instance? Identifies the concrete client, e.g. a data app name.
   */
  metabaseClientRequestIdentifier: undefined,

  /**
   * Which setting indicates whether the embedding is enabled?
   */
  enableEmbeddingSettingKey: "enable-embedding-modular" satisfies SettingKey,

  /**
   * Which token feature indicates whether the embedding is available?
   */
  tokenFeatureKey: "embedding_sdk" satisfies TokenFeature,
};

export const EMBEDDING_SDK_IFRAME_EMBEDDING_CONFIG = {
  /** Whether we are in the simple embedding environment. */
  isSimpleEmbedding: false,

  /** Whether we should use the existing user session (i.e. admin user's cookie) */
  useExistingUserSession: false,
};

/**
 * Whether we are in the Embedding SDK or its derivatives
 * such as sdk-based iframe embedding.
 **/
export const isEmbeddingSdk = () => EMBEDDING_SDK_CONFIG.isEmbeddingSdk;

/**
 * Whether we are in the MCP Apps context.
 */
export const isEmbeddingMcpApp = () => EMBEDDING_SDK_CONFIG.isMcpApp;

/**
 * Whether we are in the Embedded Analytics JS.
 */
export const isEmbeddingEajs = () =>
  EMBEDDING_SDK_IFRAME_EMBEDDING_CONFIG.isSimpleEmbedding;

/**
 * Whether we are in the Data App context.
 */
export const isDataApp = () => EMBEDDING_SDK_CONFIG.isDataApp;

/**
 * Whether we are in the Data App context.
 */
export const isDataAppDev = () => EMBEDDING_SDK_CONFIG.isDataAppDev;
